import { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import { Download, ExternalLink, FileText, Home as HomeIcon, Menu } from "lucide-react";
import { PDFViewer } from "@/components/PDFViewer";
import { Button } from "@/components/ui/button";
import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { SearchBar } from "@/components/SearchBar";
import { SidebarNavigation } from "@/components/SidebarNavigation";
import { TableOfContents } from "@/components/TableOfContents";
import { BackToTop } from "@/components/BackToTop";
import { WelcomePage } from "@/components/WelcomePage";
import { DecisionTreeViewer } from "@/components/DecisionTreeViewer";
import { ModulePage } from "@/components/learning/ModulePage";
import { useCMS } from "@/contexts/CMSContext";
import type { DecisionTree } from "@/types/cms";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "wouter";
import * as fullReportData from "@/lib/fullReportData";


// Lazy load heavy components to reduce initial bundle size
const DataVisualisation = lazy(() => import("@/components/DataVisualisations").then(m => ({ default: m.DataVisualisation })));
const AppendixContent = lazy(() => import("@/components/AppendixContent").then(m => ({ default: m.AppendixContent })));

/**
 * Strip Google Docs custom header ID syntax from markdown content.
 * Google Docs exports headings with {#h.xxxxx} IDs that aren't supported
 * by standard markdown renderers. rehype-slug will auto-generate IDs anyway.
 */
const stripGoogleDocsHeaderIds = (content: string): string => {
  // Match {#h.xxxxxxx} patterns at end of heading lines
  return content.replace(/\s*\{#h\.[a-z0-9]+\}/gi, '');
};

/**
 * Check if content has markdown headings (H2-H4).
 * Used to determine whether to show TableOfContents.
 */
const hasHeadings = (content: string): boolean => {
  return /^#{2,4}\s+.+/m.test(content);
};

// Separate component for visualisations to enable code splitting
function VisualisationsTab() {
  const { VISUALISATION_DATA } = fullReportData;

  return (
    <>
      <div className="max-w-4xl space-y-4">
        <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
          Data Visualisations
        </h2>
        <p className="text-lg text-muted-foreground leading-relaxed">
          Key findings from the national survey of 24 respondents across First Nations GP training roles and settings.
        </p>
      </div>
      <Separator className="bg-border/60" />
      <div className="grid gap-8 md:grid-cols-2">
        {VISUALISATION_DATA.map((vis: any, idx: number) => (
          <DataVisualisation key={idx} visualisation={vis} />
        ))}
      </div>
      <Card className="bg-accent/10 border-accent/20">
        <CardHeader>
          <CardTitle className="font-heading">About This Data</CardTitle>
          <CardDescription>Survey methodology and limitations</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            This scoping project involved 24 respondents across two complementary survey streams: 4 First Nations GP registrars, fellows, and supervisors (via GPRA), and 20 GP supervisors and Aboriginal health organisation staff (via GPSA).
          </p>
        </CardContent>
      </Card>
    </>
  );
}

export default function Home() {
  const { settings, tabs, folders, decisionTrees, isLoading } = useCMS();
  const { isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const [showWelcome, setShowWelcome] = useState(true);
  const [activeTree, setActiveTree] = useState<DecisionTree | null>(null);
  const [activeTab, setActiveTab] = useState("acknowledgement");
  const [activeLearningModule, setActiveLearningModule] = useState<string | null>(null);
  // When the user clicks a section box on the welcome page ("TPA Resources",
  // "Practice Manager resources") we navigate to the section heading in the
  // sidebar rather than a specific tab. These two flags track which section
  // heading is highlighted; both null => a specific tab/module/tree is active.
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [learningProgramActive, setLearningProgramActive] = useState(false);
  // Drives the Downloads accordion in the sidebar. Set true when the user
  // clicks the welcome page "Find downloads" tile; cleared when they navigate
  // anywhere else.
  const [downloadsActive, setDownloadsActive] = useState(false);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [pendingSearch, setPendingSearch] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  // Derived state — must come before callbacks that reference these values
  const visibleTabs = useMemo(() => tabs.filter(t => t.is_visible && (!t.requires_auth || isAuthenticated)), [tabs, isAuthenticated]);
  const sortedFolders = useMemo(() => [...folders].sort((a, b) => a.order_index - b.order_index), [folders]);
  const tabsByFolder = useMemo(() => {
    const map = new Map<string | null, typeof visibleTabs>();
    visibleTabs.forEach(tab => {
      const key = tab.folder_id ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(tab);
    });
    map.forEach(folderTabs => {
      folderTabs.sort((a: { label: string }, b: { label: string }) => a.label.localeCompare(b.label));
    });
    return map;
  }, [visibleTabs]);
  const standaloneTabs = useMemo(() => (tabsByFolder.get(null) || []), [tabsByFolder]);
  const downloadsFolderId = useMemo(() => folders.find(f => f.slug === 'downloads')?.id ?? null, [folders]);

  // PM-focused folder slugs — these are hidden from the TPA sidebar
  // but shown as a link list on the TPA welcome page
  const PM_FOLDER_SLUGS = useMemo(() => new Set(['handbook', 'ntcer', 'resources-pm', 'downloads']), []);

  // Resolve the canonical tab IDs for Handbook + NTCER so the welcome page can
  // link to them by slug regardless of how IDs change between resets/migrations.
  const handbookTabId = useMemo(
    () => visibleTabs.find(t => t.slug === 'handbook')?.id,
    [visibleTabs]
  );
  const ntcerTabId = useMemo(
    () => visibleTabs.find(t => t.slug === 'ntcer')?.id,
    [visibleTabs]
  );

  const pmResources = useMemo(() => {
    if (!isAuthenticated) return [];
    const pmFolderIds = new Set(
      folders.filter(f => PM_FOLDER_SLUGS.has(f.slug)).map(f => f.id)
    );
    return visibleTabs
      .filter(t => t.folder_id && pmFolderIds.has(t.folder_id) && !t.external_url)
      .map(t => ({ id: t.id, label: t.label }));
  }, [isAuthenticated, folders, visibleTabs, PM_FOLDER_SLUGS]);

  const dismissWelcome = useCallback(() => {
    setShowWelcome(false);
    setActiveTree(null);
  }, []);

  const handleSelectTree = useCallback((tree: DecisionTree) => {
    setActiveTree(tree);
    setShowWelcome(false);
    setActiveFolderId(null);
    setLearningProgramActive(false);
    setDownloadsActive(false);
  }, []);

  const handleBackFromTree = useCallback(() => {
    setActiveTree(null);
    setShowWelcome(true);
  }, []);

  const handleTreeNavigateToTab = useCallback((tabSlug: string) => {
    const targetTab = tabs.find(t => t.slug === tabSlug);
    if (targetTab) {
      setActiveTree(null);
      setShowWelcome(false);
      if (targetTab.folder_id) {
        setExpandedFolders(prev => {
          const next = new Set(prev);
          next.add(targetTab.folder_id!);
          return next;
        });
      }
      setActiveTab(targetTab.id);
    }
  }, [tabs]);

  const handleSearchFocus = useCallback(() => {
    setShowWelcome(false);
    requestAnimationFrame(() => {
      const input = document.querySelector<HTMLInputElement>('[data-search-input]');
      input?.focus();
    });
  }, []);

  // Click handler for the welcome page "Find downloads" tile.
  // Dismisses the welcome view and asks the sidebar to expand its Downloads
  // accordion (a virtual aggregator of every visible tab with a file_url,
  // alphabetised). User then picks the doc they want from the list.
  const handleFindDownloads = useCallback(() => {
    setShowWelcome(false);
    setActiveTree(null);
    setActiveLearningModule(null);
    setActiveTab("");
    setActiveFolderId(null);
    setLearningProgramActive(false);
    setDownloadsActive(true);
  }, []);

  const handleSelectLearningModule = useCallback((moduleId: string) => {
    setActiveLearningModule(moduleId);
    setShowWelcome(false);
    setActiveTree(null);
    setActiveFolderId(null);
    setLearningProgramActive(false);
    setDownloadsActive(false);
  }, []);

  // Navigate to the Learning Program section heading (module-based, not a
  // handbook folder). Expands the LP accordion; user picks a module from there.
  const handleOpenLearningProgramSection = useCallback(() => {
    setShowWelcome(false);
    setActiveTree(null);
    setActiveLearningModule(null);
    setActiveTab("");
    setActiveFolderId(null);
    setLearningProgramActive(true);
    setDownloadsActive(false);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    const trigger = document.querySelector<HTMLButtonElement>('[data-sidebar="trigger"]');
    trigger?.click();
  }, []);

  const showWelcomePage = useCallback(() => {
    // Clear ALL non-welcome view states so the welcome page actually renders.
    // Previously, clicking "Welcome" while inside an Interactive Guide left
    // activeTree set, and the activeTree branch wins the render order — so
    // showWelcome=true had no visible effect.
    setShowWelcome(true);
    setActiveTree(null);
    setActiveLearningModule(null);
    setActiveTab("");
    setActiveFolderId(null);
    setLearningProgramActive(false);
    setDownloadsActive(false);
  }, []);

  // Scroll to top when switching tabs
  // Update browser tab title from CMS settings
  useEffect(() => {
    if (settings.app_title) {
      document.title = settings.app_title;
    }
  }, [settings.app_title]);

  useEffect(() => {
    mainRef.current?.scrollTo(0, 0);
    window.scrollTo(0, 0);
  }, [activeTab]);

  // Auto-expand the folder containing the active tab
  useEffect(() => {
    const activeTabData = visibleTabs.find(t => t.id === activeTab);
    if (activeTabData?.folder_id) {
      setExpandedFolders(prev => {
        if (prev.has(activeTabData.folder_id!)) return prev;
        const next = new Set(prev);
        next.add(activeTabData.folder_id!);
        return next;
      });
    }
  }, [activeTab, visibleTabs]);

  // Wrap setActiveTab so clicking any sidebar item also dismisses the welcome page
  const handleSetActiveTab = useCallback((tabId: string) => {
    if (showWelcome || activeTree) dismissWelcome();
    setActiveTab(tabId);
    setActiveLearningModule(null); // clear learning module when switching to a tab
    setActiveFolderId(null); // a specific tab is now active; clear folder highlight
    setLearningProgramActive(false);
  }, [showWelcome, activeTree, dismissWelcome]);

  // Navigate to a folder section heading without opening a specific tab.
  // Wired to the "In this hub" boxes on the welcome page.
  const handleSelectFolder = useCallback((folderSlug: string) => {
    const folder = folders.find(f => f.slug === folderSlug);
    if (!folder) return;
    setActiveTree(null);
    setActiveLearningModule(null);
    setShowWelcome(false);
    setActiveTab("");
    setLearningProgramActive(false);
    setActiveFolderId(folder.id);
  }, [folders]);

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  // Ensure active tab exists in current tabs list. Skip when a section heading
  // is active (activeTab is deliberately empty in that case) or during the
  // welcome/module/tree views, which have no tab to resolve.
  useEffect(() => {
    if (isLoading || tabs.length === 0) return;
    if (activeFolderId || learningProgramActive) return;
    if (showWelcome || activeTree || activeLearningModule) return;
    const tabExists = tabs.find(t => t.id === activeTab);
    if (!tabExists) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, isLoading, activeTab, activeFolderId, learningProgramActive, showWelcome, activeTree, activeLearningModule]);

  // Scroll to matched search text after tab switch
  useEffect(() => {
    if (!pendingSearch) return;

    const query = pendingSearch;
    setPendingSearch(null);

    // Wait for the tab content to render
    requestAnimationFrame(() => {
      setTimeout(() => {
        const container = contentRef.current;
        if (!container) return;

        const lowerQuery = query.toLowerCase();
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);

        while (walker.nextNode()) {
          const node = walker.currentNode;
          if (node.textContent?.toLowerCase().includes(lowerQuery)) {
            const el = node.parentElement;
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              el.style.transition = 'background-color 0.3s';
              el.style.backgroundColor = 'rgba(234, 179, 8, 0.3)';
              setTimeout(() => {
                el.style.backgroundColor = '';
              }, 3000);
            }
            break;
          }
        }
      }, 100);
    });
  }, [activeTab, pendingSearch]);

  const handleSearchSelect = (sectionKey: string, searchQuery?: string) => {
    if (showWelcome) dismissWelcome();
    // Auto-expand the folder containing the target tab
    const targetTab = visibleTabs.find(t => t.id === sectionKey);
    if (targetTab?.folder_id) {
      setExpandedFolders(prev => {
        const next = new Set(prev);
        next.add(targetTab.folder_id!);
        return next;
      });
    }
    setActiveTab(sectionKey);
    if (searchQuery) {
      setPendingSearch(searchQuery);
    }
  };

  // Handle anchor link clicks within markdown content
  // Supports same-tab anchors (#heading-id) and cross-tab links (#tab:slug or #tab:slug#heading-id)
  const handleAnchorClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#tab:')) {
      e.preventDefault();
      // Cross-tab link: #tab:slug or #tab:slug#heading-id
      const withoutPrefix = href.slice(5); // remove '#tab:'
      const hashIndex = withoutPrefix.indexOf('#');
      const targetSlug = hashIndex >= 0 ? withoutPrefix.slice(0, hashIndex) : withoutPrefix;
      const targetHeading = hashIndex >= 0 ? withoutPrefix.slice(hashIndex + 1) : null;

      const targetTab = tabs.find(t => t.slug === targetSlug);
      if (targetTab) {
        // Auto-expand the folder containing the target tab
        if (targetTab.folder_id) {
          setExpandedFolders(prev => {
            const next = new Set(prev);
            next.add(targetTab.folder_id!);
            return next;
          });
        }
        setActiveTab(targetTab.id);
        if (targetHeading) {
          // Wait for tab content to render, then scroll to heading
          requestAnimationFrame(() => {
            setTimeout(() => {
              const el = document.getElementById(targetHeading);
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 150);
          });
        }
      }
    } else if (href.startsWith('#')) {
      e.preventDefault();
      const targetId = href.slice(1);
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [tabs]);

  if (isLoading) {
    return <div className="flex h-screen items-center justify-center">Loading...</div>;
  }

  return (
    <>
      {/* Left Sidebar Navigation */}
      <SidebarNavigation
        folders={sortedFolders}
        tabs={visibleTabs}
        activeTab={activeTab}
        setActiveTab={handleSetActiveTab}
        showWelcome={showWelcome}
        onShowWelcome={showWelcomePage}
        activeLearningModule={activeLearningModule}
        onSelectLearningModule={handleSelectLearningModule}
        decisionTrees={decisionTrees}
        activeTreeId={activeTree?.id ?? null}
        onSelectTree={handleSelectTree}
        activeFolderId={activeFolderId}
        learningProgramActive={learningProgramActive}
        downloadsActive={downloadsActive}
      />

      {/* Main Content Area */}
      <SidebarInset>
        {/* Dark ACRRM Header */}
        <header className="sticky top-0 z-50 w-full border-b bg-[var(--color-acrrm-dark)] backdrop-blur">
          <div className="flex h-16 items-center gap-4 px-6">
            <SidebarTrigger className="text-white" />
            {/* Home button — always visible, returns to welcome page regardless of */}
            {/* sidebar state. Critical for PMs/TPAs who navigate deep into the sidebar */}
            {/* and lose track of how to find the Handbook/NTCER tiles + feedback CTAs. */}
            <button
              onClick={showWelcomePage}
              aria-label="Home"
              title="Home"
              className={`flex h-9 w-9 items-center justify-center rounded-md transition-colors ${
                showWelcome
                  ? 'bg-white/15 text-white'
                  : 'text-white/85 hover:bg-white/10 hover:text-white'
              }`}
            >
              <HomeIcon className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-xl font-bold text-white">
                {settings.app_title || 'ACRRM Resources'}
              </h1>
              {settings.header_subtitle && (
                <p className="hidden md:block text-sm text-white/80">
                  {settings.header_subtitle}
                </p>
              )}
            </div>
            <div className="ml-auto">
              <SearchBar onResultSelect={handleSearchSelect} />
            </div>
          </div>
        </header>

        {/* Content */}
        <main ref={mainRef} className="flex-1 overflow-auto">
          {activeTree ? (
            <div className="container">
              <DecisionTreeViewer
                key={activeTree.id}
                tree={activeTree}
                onBack={handleBackFromTree}
                onNavigateToTab={handleTreeNavigateToTab}
                isAuthenticated={isAuthenticated}
              />
            </div>
          ) : showWelcome ? (
            <div className="container">
              <WelcomePage
                onGetStarted={dismissWelcome}
                onSearchFocus={handleSearchFocus}
                onFindDownloads={handleFindDownloads}
                onToggleSidebar={handleToggleSidebar}
                onNavigate={setLocation}
                onSelectTree={handleSelectTree}
                onSelectTab={handleSetActiveTab}
                onSelectFolder={handleSelectFolder}
                decisionTrees={decisionTrees}
                pmResources={pmResources}
                banner={settings.welcome_banner}
                isAuthenticated={isAuthenticated}
                onOpenLearningProgram={handleOpenLearningProgramSection}
                handbookTabId={handbookTabId}
                ntcerTabId={ntcerTabId}
              />
            </div>
          ) : activeLearningModule ? (
            <div className="container py-8 md:py-12 max-w-4xl">
              <ModulePage
                moduleId={activeLearningModule}
                onBack={() => {
                  setActiveLearningModule(null);
                  setShowWelcome(true);
                }}
              />
            </div>
          ) : (activeFolderId || learningProgramActive) && !activeTab ? (
            <div className="container py-16 md:py-24">
              <div className="max-w-xl mx-auto text-center space-y-3">
                <Menu className="h-10 w-10 text-muted-foreground/50 mx-auto" />
                <h2 className="font-heading text-2xl font-semibold text-foreground">
                  {learningProgramActive ? "Pick a module from the sidebar" : "Pick a resource from the sidebar"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {learningProgramActive
                    ? "The Learning Program modules are listed on the left. Choose one to begin."
                    : "The section you selected is expanded on the left. Pick an item to view its content."}
                </p>
              </div>
            </div>
          ) : (
          <div className="container py-8 md:py-12">
            <Tabs value={activeTab} onValueChange={handleSetActiveTab} className="space-y-8">

          {/* Content Rendering */}
          {tabs.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              
              {/* Special Case: Visualisations */}
              {tab.slug === 'visualisations' ? (
                <Suspense fallback={<div className="flex items-center justify-center py-12">Loading visualisations...</div>}>
                  <VisualisationsTab />
                </Suspense>
              ) : tab.slug === 'appendices' ? (
                /* Special Case: Appendices / Research */
                <Suspense fallback={<div className="flex items-center justify-center py-12">Loading content...</div>}>
                  <AppendixContent />
                </Suspense>
              ) : tab.folder_id === downloadsFolderId && downloadsFolderId ? (
                /* Downloads folder: centered list with direct download buttons */
                <>
                  <div className="max-w-3xl mx-auto space-y-4 text-center">
                    <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                      Downloads
                    </h2>
                    <p className="text-muted-foreground">
                      Click any item below to download it directly.
                    </p>
                  </div>

                  <Separator className="bg-border/60" />

                  <div className="max-w-3xl mx-auto space-y-3">
                    {(tabsByFolder.get(downloadsFolderId) || []).map((dlTab) => (
                      <Card key={dlTab.id} className="transition-colors hover:border-[var(--color-acrrm-orange)]/40 hover:shadow-md">
                        <CardContent className="py-4 px-5">
                          <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-[var(--color-acrrm-orange)]/10 flex items-center justify-center">
                              <FileText className="h-5 w-5 text-[var(--color-acrrm-orange)]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-heading font-semibold text-sm text-foreground leading-tight">
                                {dlTab.label}
                              </h3>
                              {dlTab.content && (
                                <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                  {dlTab.content.replace(/^#+\s+/gm, '').replace(/[*_`[\]]/g, '').split('\n').find(l => l.trim()) || ''}
                                </p>
                              )}
                            </div>
                            {dlTab.file_url ? (
                              <a
                                href={dlTab.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                              >
                                <Button size="sm" className="gap-1.5 flex-shrink-0">
                                  <Download className="h-4 w-4" />
                                  Download
                                </Button>
                              </a>
                            ) : (
                              <span className="text-xs text-muted-foreground italic flex-shrink-0">Coming soon</span>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              ) : tab.file_url && tab.file_url.toLowerCase().endsWith('.pdf') ? (
                /* PDF tabs: inline PDF viewer, no markdown/TOC */
                <>
                  <div className="max-w-4xl space-y-4">
                    <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                      {tab.label}
                    </h2>
                  </div>

                  <Separator className="bg-border/60" />

                  <div className="w-full">
                    <PDFViewer fileUrl={tab.file_url} title={tab.label} />
                  </div>
                </>
              ) : tab.file_url ? (
                /* Non-PDF downloadable files: TOC + Download side-by-side */
                <>
                  <div className="max-w-4xl space-y-4">
                    <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                      {tab.label}
                    </h2>
                  </div>

                  <Separator className="bg-border/60" />

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <TableOfContents content={stripGoogleDocsHeaderIds(tab.content)} maxDepth={tab.toc_max_depth ?? undefined} />
                    </div>
                    <Card className="bg-accent/5 border-accent/20">
                      <CardContent className="pt-6">
                        <div className="flex flex-col items-center justify-center gap-4">
                          <a
                            href={tab.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                          >
                            <Button size="lg" className="gap-2">
                              <Download className="h-5 w-5" />
                              Download {tab.label}
                            </Button>
                          </a>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <Card className="bg-card shadow-sm">
                    <CardContent className="pt-6">
                      <div ref={contentRef} className="prose prose-stone dark:prose-invert max-w-none prose-headings:font-heading prose-a:text-primary prose-strong:text-primary">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeSlug]}
                          components={{
                            a: ({ href, children, ...props }) => (
                              <a
                                href={href}
                                onClick={(e) => href && handleAnchorClick(e, href)}
                                {...props}
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {stripGoogleDocsHeaderIds(tab.content)}
                        </ReactMarkdown>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : tab.external_url ? (
                /* External URL Resource Page */
                <>
                  <div className="max-w-3xl mx-auto space-y-6 text-center py-8">
                    <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                      {tab.label}
                    </h2>

                    <Separator className="bg-border/60" />

                    {tab.description && (
                      <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                        {tab.description}
                      </p>
                    )}

                    <div className="pt-4">
                      <a
                        href={tab.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button size="lg" className="gap-2">
                          <ExternalLink className="h-5 w-5" />
                          Visit this page on ACRRM.org.au
                        </Button>
                      </a>
                    </div>

                    <p className="text-sm text-muted-foreground italic">
                      This resource is maintained by ACRRM and opens on their website.
                    </p>
                  </div>
                </>
              ) : (
                /* Standard Content Tabs */
                <>
                  <div className="max-w-4xl space-y-4">
                    <h2 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
                      {tab.label}
                    </h2>
                  </div>

                  <Separator className="bg-border/60" />

                  <TableOfContents content={stripGoogleDocsHeaderIds(tab.content)} maxDepth={tab.toc_max_depth ?? undefined} />

                  <Card className="bg-card shadow-sm">
                    <CardContent className="pt-6">
                      <div ref={contentRef} className="prose prose-stone dark:prose-invert max-w-none prose-headings:font-heading prose-a:text-primary prose-strong:text-primary">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeSlug]}
                          components={{
                            a: ({ href, children, ...props }) => (
                              <a
                                href={href}
                                onClick={(e) => href && handleAnchorClick(e, href)}
                                {...props}
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {stripGoogleDocsHeaderIds(tab.content)}
                        </ReactMarkdown>
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </TabsContent>
          ))}
        </Tabs>
          </div>
          )}
        </main>

        {/* Footer */}
        <footer className="border-t bg-muted/50 mt-16">
          <div className="container py-8">
            {settings.footer_sections && settings.footer_sections.length > 0 && (
              <>
                <div className={`grid gap-8 ${settings.footer_sections.length === 1 ? '' : settings.footer_sections.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                  {settings.footer_sections.map((section, index) => (
                    <div key={index}>
                      <h4 className="font-heading font-bold mb-3">{section.title}</h4>
                      <div className="text-sm text-muted-foreground prose prose-sm prose-stone max-w-none [&_ul]:list-disc [&_ul]:pl-4 [&_ul]:space-y-1 [&_li]:m-0 [&_p]:m-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripGoogleDocsHeaderIds(section.content)}</ReactMarkdown>
                      </div>
                    </div>
                  ))}
                </div>
                <Separator className="my-6" />
              </>
            )}
            <p className="text-xs text-muted-foreground text-center">
              {settings.footer_text}
            </p>
          </div>
        </footer>
      </SidebarInset>

      {/* Back to Top Button */}
      <BackToTop />
    </>
  );
}
